import { FlaskConical, X } from 'lucide-react';

import type { Persona } from '@repo/contracts';
import { Tooltip } from '@repo/ui/tooltip';

import { usePersonaLookalikes } from '../persona-lookalikes/use-persona-lookalikes';

const MIN_COUNT = 100;
const MAX_COUNT = 1000;

/**
 * The persona and population size a lookalike poll will run against — the
 * question itself is typed in the shared textarea, same as a room poll.
 *
 * Count is only ever chosen once: a persona with an existing spec has it
 * locked to that spec's own count, since generating again would re-draw the
 * whole trait schema rather than resize it (see `generate()`'s own doc).
 */
export function LookalikePollEditor({
  count,
  onClose,
  onCountChange,
  onPersonaChange,
  personaId,
  personas,
}: {
  count: number;
  onClose: () => void;
  onCountChange: (count: number) => void;
  onPersonaChange: (personaId: string | null) => void;
  personaId: string | null;
  personas: Persona[];
}) {
  const { loading, view } = usePersonaLookalikes(personaId);

  return (
    <div
      aria-label="Lookalike poll"
      className="mb-2 rounded-card bg-surface p-3 ring-1 ring-hairline-alt duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none"
      role="group"
    >
      <div className="flex items-center gap-1">
        <FlaskConical
          aria-hidden="true"
          className="mr-1 size-4 shrink-0 text-brand"
        />
        <p className="flex-1 truncate text-tag font-semibold text-text-secondary">
          Ask a population of lookalikes
        </p>
        <Tooltip label="Remove — ask as a normal question">
          <button
            aria-label="Remove lookalike poll"
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-muted transition hover:bg-surface-hover hover:text-text-primary"
            onClick={onClose}
            type="button"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </Tooltip>
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <select
          aria-label="Persona"
          className="h-8 min-w-0 flex-1 rounded-button bg-warm px-2.5 text-ui text-text-primary outline-none transition focus:bg-surface focus:ring-2 focus:ring-brand/40"
          onChange={(event) => {
            onPersonaChange(event.target.value || null);
          }}
          value={personaId ?? ''}
        >
          <option value="">Choose a persona…</option>
          {personas.map((persona) => (
            <option key={persona.id} value={persona.id}>
              {persona.name}
            </option>
          ))}
        </select>

        {!loading && view ? (
          <span className="shrink-0 text-tag whitespace-nowrap text-text-secondary">
            Using existing population of{' '}
            <span className="font-semibold text-text-primary">
              {view.spec.count}
            </span>
          </span>
        ) : (
          <label className="flex shrink-0 items-center gap-1.5 text-tag text-text-secondary">
            Count
            <input
              aria-label="How many lookalikes"
              className="h-8 w-20 rounded-button bg-warm px-2 text-ui text-text-primary outline-none transition focus:bg-surface focus:ring-2 focus:ring-brand/40"
              max={MAX_COUNT}
              min={MIN_COUNT}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isFinite(next)) onCountChange(next);
              }}
              type="number"
              value={count}
            />
          </label>
        )}
      </div>

      <p className="mt-2 px-1 text-tag text-text-secondary">
        Only you see the result — the room&rsquo;s personas never hear it. A
        single yes / no question only.
      </p>
    </div>
  );
}
